import React, { useState, useEffect } from "react";
import { autoFormatNumber } from "../lib/utils";
import { TABWIDTH } from "../lib/constants";

interface CustomerReviewProps {
  averageRating?: string;
  totalRatingCount?: string;
  isLoading: boolean;
}

const CustomerReview: React.FC<CustomerReviewProps> = ({
  averageRating,
  totalRatingCount,
  isLoading,
}) => {
  const windowWidth = window.innerWidth;
  const [randomRatingCount, setRandomRatingCount] = useState<number | null>(
    null
  );
  const [displayedRatingCount, setDisplayedRatingCount] = useState<string>("0");
  const [displayedAverageRating, setDisplayedAverageRating] =
    useState<string>("0");

  useEffect(() => {
    if (randomRatingCount === null) {
      const min = 1200;
      const max = 1299;
      setRandomRatingCount(Math.floor(Math.random() * (max - min + 1)) + min);
    }
  }, []);

  // Separate useEffect for count animation
  useEffect(() => {
    if (isLoading) {
      let count = 0;
      const target = randomRatingCount || 1200;
      const increment = Math.ceil(target / 100);

      const interval = setInterval(() => {
        count += increment;
        if (count >= target) {
          count = target;
          clearInterval(interval);
        }
        setDisplayedRatingCount(autoFormatNumber(count));
      }, 20);

      return () => clearInterval(interval);
    } else {
      const parsedRatingCount = totalRatingCount
        ? parseInt(totalRatingCount, 10)
        : 0;
      const finalCount =
        parsedRatingCount < 1200
          ? autoFormatNumber(randomRatingCount || 1200)
          : autoFormatNumber(parsedRatingCount);
      setDisplayedRatingCount(finalCount);
    }
  }, [isLoading, totalRatingCount, randomRatingCount]);

  // Separate useEffect for rating animation
  useEffect(() => {
    if (isLoading) {
      setDisplayedAverageRating("0.0");
      const targetRating = parseFloat(averageRating || "5");
      let steps = 0;
      const totalSteps = 50;

      const ratingInterval = setInterval(() => {
        steps++;
        const progress = steps / totalSteps;
        const currentRating = (targetRating * progress).toFixed(1);

        if (steps >= totalSteps) {
          clearInterval(ratingInterval);
          setDisplayedAverageRating(targetRating.toFixed(1));
        } else {
          setDisplayedAverageRating(currentRating);
        }
      }, 40);

      return () => clearInterval(ratingInterval);
    } else {
      const finalRating = averageRating || "5";
      setDisplayedAverageRating(finalRating);
    }
  }, [isLoading, averageRating]);

  return (
    <div
      className={
        windowWidth < TABWIDTH
          ? "w-[82%] absolute rounded-xl bg-white bottom-0 left-1/2 transform -translate-x-1/2 translate-y-[60%] bg-opacity-100 backdrop-blur text-black"
          : windowWidth < 1237
          ? "absolute rounded-md top-1/2 bg-white left-1/2 transform -translate-x-[30%] translate-y-[25%] xxl:translate-y-[45%] max-w-[35%] max-h-[28%] object-contain z-20 bg-opacity-90 backdrop-blur text-black"
          : windowWidth < 1380
          ? "absolute rounded-md top-1/2 bg-white left-1/2 transform -translate-x-[25%] translate-y-[50%] xxl:translate-y-[75%] max-w-[43%] max-h-[33%] object-contain z-20 bg-opacity-90 backdrop-blur text-black"
          : "absolute rounded-md top-1/2 bg-white left-1/2 transform -translate-x-[25%] translate-y-[75%] xxl:translate-y-[75%] max-w-[43%] max-h-[33%] object-contain z-20 bg-opacity-90 backdrop-blur text-black"
      }
    >
      <div className="py-3 px-7 rounded-lg flex flex-col items-center shadow-[0px_4px_8px_rgba(0,0,0,0.15)]">
        <h2 className="text-base font-bold mb-0.5">Happy Customers</h2>
        <div className="flex items-center gap-1">
          <span className="text-yellow-500">
            <img
              src="/Images/HomepageImages/FirstSection/star.png"
              alt="star"
              width={16}
              height={16}
            />
          </span>
          <span className="text-2xl font-semibold">
            {displayedAverageRating}
          </span>
          <span>{"  "}</span>
          <span className="text-2xl font-bold tracking-wide">
            {displayedRatingCount}{" "}
            <span className="text-sm font-normal">Reviews</span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default CustomerReview;
